import React, { useState } from 'react';
import { MapPin, ExternalLink, ChevronDown, ChevronUp, Trophy, Users, Calendar, Heart } from 'lucide-react';
import { indianCricketPathway, topCricketAcademies, internationalPathways, domesticTournaments } from '../data/cricketAcademies';
import { womenCricketPathway } from '../data/cricketLegends';

const AcademiesSection = () => {
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [activeTab, setActiveTab] = useState('pathway');

  const tabs = [
    { id: 'pathway', label: 'Indian Pathway', icon: Trophy },
    { id: 'women', label: "Women's Pathway", icon: Heart },
    { id: 'academies', label: 'Top Academies', icon: Users },
    { id: 'tournaments', label: 'Domestic Tournaments', icon: Calendar },
    { id: 'international', label: 'Global Pathways', icon: ExternalLink }
  ];

  return (
    <section id="academies" className="bg-white py-12 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black tracking-[0.05em] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Cricket Career Pathway
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Your complete guide to cricket academies, tournaments, and the journey from grassroots to international cricket
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <tab.icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* Indian Pathway */}
        {activeTab === 'pathway' && (
          <div className="space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold mb-6 text-center">{indianCricketPathway.title}</h3>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block"></div>
              
              {indianCricketPathway.levels.map((level, index) => (
                <div key={level.id} className="relative mb-4">
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-4 h-4 bg-black rounded-full hidden md:block" style={{ top: '24px' }}></div>
                  
                  <div
                    className="md:ml-16 bg-gray-50 rounded-lg overflow-hidden cursor-pointer"
                    onClick={() => setExpandedLevel(expandedLevel === level.id ? null : level.id)}
                  >
                    <div className="flex items-center justify-between p-5">
                      <div className="flex items-center gap-4">
                        <span className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                          {level.id}
                        </span>
                        <div>
                          <h4 className="font-semibold text-gray-900">{level.name}</h4>
                          <p className="text-sm text-gray-500">{level.age}</p>
                        </div>
                      </div>
                      {expandedLevel === level.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                    
                    {expandedLevel === level.id && (
                      <div className="px-5 pb-5 border-t border-gray-200">
                        <p className="text-gray-600 mt-4 mb-4">{level.description}</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-semibold text-sm mb-2">Key Tournaments:</h5>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {level.tournaments.map((t, i) => (
                                <li key={i}>• {t}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 className="font-semibold text-sm mb-2">Selection Path:</h5>
                            <p className="text-sm text-gray-600">{level.selectionPath}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Women's Pathway */}
        {activeTab === 'women' && (
          <div className="space-y-4">
            <h3 className="text-2xl font-bold mb-6 text-center">{womenCricketPathway.title}</h3>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              The path to representing India in women's cricket is now clearer than ever. Here's your roadmap to success.
            </p>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-pink-200 hidden md:block"></div>
              
              {womenCricketPathway.levels.map((level, index) => (
                <div key={index} className="relative mb-4">
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-4 h-4 bg-pink-500 rounded-full hidden md:block" style={{ top: '24px' }}></div>
                  
                  <div className="md:ml-16 bg-pink-50 rounded-lg overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-center gap-4 mb-3">
                        <span className="bg-pink-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <div>
                          <h4 className="font-semibold text-gray-900">{level.level}</h4>
                          <p className="text-sm text-gray-500">{level.age}</p>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{level.description}</p>
                      <div>
                        <h5 className="font-semibold text-sm mb-2 text-pink-700">Opportunities:</h5>
                        <div className="flex flex-wrap gap-2">
                          {level.opportunities.map((opp, i) => (
                            <span key={i} className="bg-white text-pink-600 px-3 py-1 rounded-full text-xs border border-pink-200">
                              {opp}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Key Contacts */}
            <div className="mt-8 bg-pink-100 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-3">Key Contacts & Resources</h4>
              <ul className="space-y-2">
                {womenCricketPathway.keyContacts.map((contact, i) => (
                  <li key={i} className="text-sm text-gray-700">• {contact}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Academies */}
        {activeTab === 'academies' && (
          <div className="grid md:grid-cols-2 gap-6">
            {topCricketAcademies.map((academy) => (
              <div key={academy.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-lg text-gray-900">{academy.name}</h4>
                    <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                      <MapPin size={14} />
                      {academy.location}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs ${
                    academy.type === 'BCCI Official' ? 'bg-blue-100 text-blue-700' :
                    academy.type === 'Private Elite' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {academy.type}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4">{academy.description}</p>
                <div className="mb-3">
                  <h5 className="text-xs font-semibold text-gray-500 mb-2">FACILITIES</h5>
                  <div className="flex flex-wrap gap-2">
                    {academy.facilities.map((facility, i) => (
                      <span key={i} className="bg-white px-2 py-1 rounded text-xs text-gray-600">
                        {facility}
                      </span>
                    ))}
                  </div>
                </div>
                {academy.notable && (
                  <p className="text-sm text-green-700 font-medium">⭐ {academy.notable}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Domestic Tournaments */}
        {activeTab === 'tournaments' && (
          <div className="space-y-4">
            <h3 className="text-2xl font-bold mb-6 text-center">Indian Domestic Cricket Structure</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {domesticTournaments.india.map((tournament, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-3 h-3 rounded-full ${
                      tournament.format.includes('First-Class') ? 'bg-red-500' :
                      tournament.format.includes('List A') ? 'bg-blue-500' :
                      'bg-green-500'
                    }`}></span>
                    <h4 className="font-bold text-gray-900">{tournament.name}</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-500">
                      <span className="font-medium">Format:</span> {tournament.format}
                    </p>
                    <p className="text-gray-500">
                      <span className="font-medium">Teams:</span> {tournament.teams}
                    </p>
                    <p className="text-gray-600 mt-3">{tournament.description}</p>
                    <p className="text-black font-medium mt-2">→ {tournament.importance}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-6 mt-8 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                First-Class (4-day)
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                List A (50-over)
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                T20
              </div>
            </div>
          </div>
        )}

        {/* International Pathways */}
        {activeTab === 'international' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {internationalPathways.map((country, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 text-gray-900">{country.country}</h4>
                <div className="space-y-2 mb-4">
                  {country.pathway.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs">
                        {i + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <p className="text-sm"><span className="font-medium">Key Tournament:</span> {country.keyTournament}</p>
                  <p className="text-sm text-gray-600 mt-2">{country.uniqueFeature}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AcademiesSection;
